import { useMemo, useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import { BookMark } from 'svgIcons'
import {
  NewsItemFooter_NewsItemFragment,
  NotificationScope,
  PageLayoutDocument,
  SharePlatFormType,
  useAddLikeMutation,
  useAddScrapMutation,
  useCurrentUserQuery,
  useIncrementShareCountMutation,
  useRemoveLikeMutation,
  useRemoveScrapMutation,
} from 'generated/graphql'
import SnackbarLink from '@app.components/SnackbarLink/SnackbarLink'
import ShareModal from '@app.components/modals/ShareModal'
import NewsReactionModal from '@app.feature/channel/component/modals/NewsReactionModal/NewsReactionModal'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import { TChannelOgData } from '@app.feature/channel/module/getChannelOgData'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { mobileBreakPoint } from 'app.styled/media'
import LoginAlert from 'components/LoginAlert'
import Comment from 'svgIcons/Comment'
import Like from 'svgIcons/Like'
import Scrap from 'svgIcons/Scrap'
import ShareLight from 'svgIcons/ShareLight'

gql`
  fragment NewsItemFooter_NewsItem on NewsItem {
    id
    likers {
      totalCount
    }
    comments {
      totalCount
    }
    channel {
      id
      url
      viewerHasFollowed
    }
    shareCount
    viewerHasLiked
    bookmarkCount
    viewerHasBookmarked
    newsCommentCount
  }
`
type TProps = {
  data: NewsItemFooter_NewsItemFragment
  isCommentListOpened?: boolean
  onCommentBtnClick?: React.MouseEventHandler<HTMLButtonElement>
  type?: 'feed' | 'modal' | 'card'
  className?: string
  ogData?: TChannelOgData
}

const NewsItemFooter: React.FC<TProps> = ({
  type = 'feed',
  data,
  isCommentListOpened = false,
  onCommentBtnClick,
  className,
  ogData,
}) => {
  const router = useRouter()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const onError = useChannelError()
  const currentUserQuery = useCurrentUserQuery()
  const addLikeMutation = useAddLikeMutation({ onError })
  const removeLikeMutation = useRemoveLikeMutation({ onError })
  const addScrapMutation = useAddScrapMutation({ onError })
  const removeScrapMutation = useRemoveScrapMutation({ onError })
  const [incrementShareCountMutate] = useIncrementShareCountMutation()

  const incrementShareCountWithPlatform = (platform: SharePlatFormType) => {
    incrementShareCountMutate({ variables: { newsItemId: data.id, platform } })
  }

  const handleLikeBtnClick = () => {
    if (!currentUserQuery.data?.currentUser) return setLoginModalOpen(true)

    if (!data.viewerHasLiked) {
      const [mutate] = addLikeMutation
      mutate({ variables: { newsItemId: `${data.id}` } })
    }
    if (data.viewerHasLiked) {
      const [mutate] = removeLikeMutation
      mutate({ variables: { newsItemId: `${data.id}` } })
    }
  }

  const handleScrapBtnClick = async () => {
    if (!currentUserQuery.data?.currentUser) return setLoginModalOpen(true)

    if (!data.viewerHasBookmarked) {
      const [mutate] = addScrapMutation

      await mutate({
        variables: { newsItemId: `${data.id}` },
        refetchQueries: [
          {
            query: PageLayoutDocument,
            variables: { notificationType: NotificationScope.NOTIFICATION },
          },
        ],
      })

      if (!data.channel?.viewerHasFollowed && data.channel) {
        enqueueSnackbar('이 게시글을 등록한 기업을 팔로우했습니다.', {
          action: <SnackbarLink href={`/channel/${data.channel.url}`} />,
        })
      }
    }
    if (data.viewerHasBookmarked) {
      const [mutate] = removeScrapMutation

      mutate({
        variables: { newsItemId: `${data.id}` },
        refetchQueries: [
          {
            query: PageLayoutDocument,
            variables: { notificationType: NotificationScope.NOTIFICATION },
          },
        ],
      })
    }
  }

  const closeReactionModal = () => {
    const { query } = router
    delete query.modal
    delete query.id

    router.push({ query: { ...query } }, undefined, { shallow: true })
  }

  const newsItemUrl = useMemo(() => {
    if (typeof window !== 'object') return ''

    return `${window.location.origin}/channel/${data.channel?.url}/${data.id}`
  }, [data])

  return (
    <>
      <StyledWrapper className={className} data-type={type}>
        <div className="counts">
          <Link
            href={
              !isWebview
                ? {
                    query: {
                      ...router.query,
                      modal: 'like',
                      id: data.id,
                    },
                  }
                : ''
            }
            shallow={true}
          >
            <a className="count-text">
              {data.likers.totalCount !== 0 &&
                `좋아요 ${data.likers.totalCount}`}
            </a>
          </Link>
          <div className="info-list">
            {data.newsCommentCount !== 0 && (
              <button className="count-text" onClick={onCommentBtnClick}>
                댓글 {data.newsCommentCount}
              </button>
            )}
            {data.shareCount !== 0 && <div>공유 {data.shareCount}</div>}
            {data.bookmarkCount !== 0 && (
              <Link
                href={
                  !isWebview
                    ? {
                        query: {
                          ...router.query,
                          modal: 'scrap',
                          id: data.id,
                        },
                      }
                    : ''
                }
                shallow={true}
              >
                <a className="count-text">스크랩 {data.bookmarkCount}</a>
              </Link>
            )}
          </div>
        </div>
        <div className="hr"></div>
        <div className="btns">
          <button
            className={`btn ${data.viewerHasLiked ? `active` : ``}`}
            onClick={handleLikeBtnClick}
          >
            <Like color={data.viewerHasLiked ? '#01a0ff' : undefined} />
            <div>{(type !== 'card' || !isMobile) && '좋아요'}</div>
          </button>
          <button
            className={`btn ${isCommentListOpened ? `active` : ``}`}
            onClick={onCommentBtnClick}
          >
            <Comment color={isCommentListOpened ? '#01a0ff' : undefined} />
            <div>{(type !== 'card' || !isMobile) && '댓글'}</div>
          </button>
          <button
            className="btn"
            onClick={() => {
              if (onError()) return
              setShareModalOpen(true)
            }}
          >
            <ShareLight />
            <div>{(type !== 'card' || !isMobile) && '공유하기'}</div>
          </button>
          <button
            className="btn scrap"
            onClick={handleScrapBtnClick}
            data-fill={data.viewerHasBookmarked}
          >
            {data.viewerHasBookmarked ? <BookMark /> : <Scrap />}
            <div>{(type !== 'card' || !isMobile) && '스크랩'}</div>
          </button>
        </div>
      </StyledWrapper>
      {shareModalOpen && (
        <ShareModal
          shareLink={newsItemUrl}
          open={shareModalOpen}
          onClick={incrementShareCountWithPlatform}
          onClose={() => setShareModalOpen(false)}
          ogImage={ogData?.image}
          title={ogData?.title}
        />
      )}
      {router.query.id === data.id && (
        <NewsReactionModal open onClose={closeReactionModal} />
      )}
      <LoginAlert
        DialogProps={{ open: loginModalOpen }}
        onRequestClose={() => setLoginModalOpen(false)}
      />
    </>
  )
}

export default NewsItemFooter

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 20px 0;

  .count-text {
    border: none;
    padding: 0px;
    background: none;
    font-size: 15px;
    line-height: 1.33;
    font-weight: 400;
  }
  .counts {
    display: flex;
    justify-content: space-between;
    font-size: 15px;
    line-height: 1.33;
    font-weight: 400;
    color: #333;
    padding: 16px 0;
    .info-list {
      display: flex;
      gap: 8px;
    }
  }
  .hr {
    height: 1px;
    background-color: #eeeeee;
  }
  .btns {
    display: flex;
    padding: 8px 0;
    .btn {
      cursor: pointer;
      background-color: white;
      border: none;
      flex-grow: 1;
      text-align: center;
      color: #777777;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 0px;
      border-radius: 5px;
      &.active {
        color: #01a0ff;
      }
      &:hover {
        background-color: #f5f5f5;
      }
      &.scrap {
        &[data-fill='true'] {
          color: #01a0ff;
          svg {
            width: 20px;
            font-size: 14px;
          }
        }
      }
    }
  }
  &[data-type='modal'] {
    .btns {
      padding-top: 4px;
      font-size: 14px;
      .btn {
        gap: 0;
      }
    }
  }
  &[data-type='card'] {
    .counts {
      padding: 8px 0;
      .info-list {
        height: 17px;
      }
    }
    .btns {
      font-size: 14px;
    }
  }
  ${mobileBreakPoint} {
    padding: 0px 15px 0;
    .btns {
      .btn {
        background-color: transparent;
        &:hover {
          background-color: transparent;
        }
      }
    }

    .count-text {
      border: none;
      padding: 0px;
      background: none;
      font-size: 15px;
      line-height: 1.33;
      font-weight: 400;
    }
    &[data-type='modal'] {
      padding: 0px 20px 0;
      .count-text {
        color: #fff;
      }
      .counts {
        color: white;
        .info-list {
          button {
            color: white;
          }
        }
      }
      .btns {
        .btn {
          color: white;
        }
      }
    }
    &[data-type='card'] {
      .btns {
        .btn {
          padding: 4px 0px;
        }
      }
    }
  }
`
